import * as React from "react"
import {
  DayPicker,
  type ChevronProps,
  type DropdownNavProps,
} from "react-day-picker"
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react"

import { cn } from "@/lib/utils"

export type CalendarProps = React.ComponentProps<typeof DayPicker>

function Calendar({ className, classNames, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays
      className={cn("text-sm", className)}
      classNames={{
        months: "relative flex flex-col",
        month: "space-y-3",
        month_caption: "flex h-8 items-center justify-center",
        caption_label: "inline-flex items-center gap-1 text-sm font-medium capitalize",
        // Over the caption, so with dropdowns there it lets their clicks through.
        nav: "pointer-events-none absolute inset-x-0 top-0 z-10 flex items-center justify-between",
        button_previous:
          "pointer-events-auto inline-flex h-8 w-8 items-center justify-center rounded-md text-[#5a6259] hover:bg-[#f0f2ee] disabled:pointer-events-none disabled:opacity-30",
        button_next:
          "pointer-events-auto inline-flex h-8 w-8 items-center justify-center rounded-md text-[#5a6259] hover:bg-[#f0f2ee] disabled:pointer-events-none disabled:opacity-30",
        dropdowns: "flex items-center gap-0.5",
        // The native select sits invisibly over its label: the label is what
        // shows, the browser's own list is what opens.
        dropdown_root:
          "relative inline-flex h-8 items-center rounded-md px-2 transition-colors hover:bg-[#f0f2ee] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#2d6a4f]/50",
        dropdown: "absolute inset-0 w-full cursor-pointer opacity-0",
        month_grid: "border-collapse",
        weekdays: "flex",
        weekday: "w-9 text-[11px] font-normal text-[#7a8077] capitalize",
        week: "mt-1 flex",
        day: "h-9 w-9 p-0 text-center",
        day_button:
          "inline-flex h-9 w-9 items-center justify-center rounded-md tabular-nums transition-colors hover:bg-[#e8efe9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2d6a4f]/50",
        selected:
          "[&>button]:bg-[#2d6a4f] [&>button]:text-white [&>button]:hover:bg-[#245741]",
        today: "[&>button]:font-semibold [&>button]:underline [&>button]:underline-offset-4",
        outside: "opacity-40",
        disabled: "[&>button]:pointer-events-none text-[#c5cdc4]",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        // Year before month, the way dates are narrowed down: DayPicker
        // renders them month first.
        DropdownNav: ({ children, ...navProps }: DropdownNavProps) => (
          <div {...navProps}>{React.Children.toArray(children).reverse()}</div>
        ),
        Chevron: ({ orientation }: ChevronProps) =>
          orientation === "left" ? (
            <ChevronLeft className="h-4 w-4" />
          ) : orientation === "down" ? (
            <ChevronDown className="h-3.5 w-3.5 text-[#7a8077]" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          ),
      }}
      {...props}
    />
  )
}
Calendar.displayName = "Calendar"

export { Calendar }
